/**
 * Where a route test's drops go (user, 19/10/2026: "Unpinned test drops ·
 * Rotate test lanes · Use first lane — I don't understand"). A drop slot can
 * have its own gateway; every other drop either takes turns through the
 * gateways (A → B → C → A…) or goes through the first one. Same rule as the
 * planner's desiredSequence for these two strategies, counted on the
 * enabled drop slots (the board's planned drops can be fewer: caps, the
 * window); at generation a gateway that is blocked or full for the day
 * passes its turn on.
 * For Media Devoted | Created By Ciro Autiero
 */
import { gwLetter } from '../cards/strategy-copy';

export type TestSpreadChoice = 'ALTERNATE' | 'HOLD_UNTIL_FAIL';

export interface TestSpreadFacts {
  slots: ReadonlyArray<{ enabled: boolean; routeId: number | null }>;
  /** The gateway rows in pool order (A, B, C…). */
  gatewayCount: number;
}

export const testSpreadChoiceOf = (strategy: string): TestSpreadChoice =>
  strategy === 'HOLD_UNTIL_FAIL' ? 'HOLD_UNTIL_FAIL' : 'ALTERNATE';

/** Drops with their own gateway, drops without, and how many of those each gateway gets. */
export function testSpread(choice: TestSpreadChoice, facts: TestSpreadFacts) {
  const enabled = facts.slots.filter((s) => s.enabled);
  const open = enabled.filter((s) => s.routeId === null).length;
  const perGateway = Array.from({ length: facts.gatewayCount }, (_, i) =>
    choice === 'HOLD_UNTIL_FAIL' ? (i === 0 ? open : 0) : Math.floor(open / facts.gatewayCount) + (i < open % facts.gatewayCount ? 1 : 0),
  );
  return { own: enabled.length - open, open, perGateway };
}

const drops = (n: number) => `${n} drop slot${n === 1 ? '' : 's'}`;

/** One line under the choice: what it does to the drop slots. */
export function testSpreadLine(choice: TestSpreadChoice, facts: TestSpreadFacts): string {
  if (facts.gatewayCount === 0) return 'Add a gateway above first.';
  const { own, open, perGateway } = testSpread(choice, facts);
  const kept = own === 0 ? '' : own === 1 ? ' 1 drop slot keeps the gateway chosen on it.' : ` ${own} drop slots keep the gateway chosen on them.`;
  if (open === 0) return `Every drop slot has its own gateway, so this choice changes nothing.${kept}`;
  if (choice === 'HOLD_UNTIL_FAIL') {
    const others = facts.gatewayCount > 1 ? ' The other gateways only get the drops you give them.' : '';
    return `All ${drops(open)} go through A.${others}${kept}`;
  }
  const shares = perGateway.map((n, i) => `${gwLetter(i)} ${n}`).join(' · ');
  return `${drops(open)} take turns: ${shares}.${kept}`;
}
